import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import moment from "moment";
import { useHistory } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDownLeft, faArrowUpLeft } from "@fortawesome/pro-solid-svg-icons";
import {
  faCheck,
  faCreditCard,
  faEllipsisVertical,
  faPlus,
  faQuestionCircle,
  faUser,
  faWallet,
} from "@fortawesome/pro-regular-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import classNames from "classnames";
import useModal from "hooks/modals/useModal";

import { StatusCode } from "api/protocols";
import {
  DownloadCustomerAccountStatements,
  EmailCustomerAccountStatement,
  GetAccountTransactionReceipt,
  GetCustomerHouseAccounts,
  GetCustomerStatements,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";
import {
  CloseHouseAccount,
  GetAccountScheduledPayments,
  CancelAccountScheduledPayment,
  GetAccountTransactions,
  IPostAccount,
  IPutAccount,
  PostAccount,
  PostAccountTransactionRefund,
  PutAccount,
  PutAccountBalanceRefresh,
  AddMinimumToAccount,
  RemoveMinimumFromAccount,
  ProcessScheduledPaymentManual,
  PutAccountTransaction,
} from "api/rpc/2024-04/facilityAdmin/client/accounts";
import { GetMinimumSpending } from "api/rpc/2024-04/facilityAdmin/accounts/minimumSpending";

import { showError, showSuccess } from "redux/actions/ui";
import { loadPaymentOptions } from "redux/actions/facility";

import { LocaleCurrency, LocaleCurrencyString } from "helpers/Locale";
import { capitalize, sortObj, valueToString } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { TABLET_WIDTH } from "helpers/ScreenSizes";

import { useCustomerContext } from "../../context/CustomerContext";
import { ButtonNew as Button } from "components/buttonNew";
import Spin from "components/spin/spin";
import { Badge } from "components/badge/Badge";
import Card from "components/card/Card";
import Popup from "components/popup/Popup";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Sheet from "components/sheet/Sheet";
import AccountScheduledPaymentModal from "./AccountScheduledPaymentModal";
import EditAccountScheduledPaymentModal from "./EditAccountScheduledPaymentModal";
import DataTable from "../../../../../../components/dataTable/DataTable";
import HouseAccountPaymentModal from "./HouseAccountPaymentModal";
import NewCustomerHouseAccountModal from "./NewCustomerHouseAccountModal";
import HouseAccountAdjustModal from "./HouseAccountAdjustModal";
import { EditCustomerHouseAccountModal, EditHouseAccountState } from "./EditCustomerHouseAccountModal";
import ManageAccountCustomersDrawer from "./ManageAccountCustomersDrawer";
import AccountCustomersCell from "pages/secure/facility/settings/accounts/AccountCustomersCell";
import {
  HouseAccountScheduledPayment,
  HouseAccountStatementType,
  HouseAccountTransactionType,
  HouseAccountType,
  TMinimumSpend,
  TransactionKind,
} from "../../context/contextTypes";

import "./CustomerHouseAccounts.scss";

import ProgressCircle from "components/progressCircle/ProgressCircle";
import Portal from "elements/Portal";
import SelectNew from "components/select/SelectNew";
import Input from "components/form/input";
import { useStripeTerminalPayment } from "hooks/useStripeTerminalPayment/useStripeTerminalPayment";

const OVERVIEW_VISIBLE_CUSTOMER_NAMES = 3;

function getAccountCustomers(account: HouseAccountType | null | undefined) {
  if (!account) {
    return [];
  }
  if (account.customers && account.customers.length > 0) {
    return account.customers;
  }
  if (account.customer) {
    return [account.customer];
  }
  return [];
}

function getOverviewAccountCustomers(account: HouseAccountType | null | undefined, viewedCustomerId?: number | null) {
  const customers = getAccountCustomers(account);
  if (!viewedCustomerId || customers.length <= 1) {
    return customers;
  }

  const viewedCustomer = customers.find(customer => customer.id === viewedCustomerId);
  if (!viewedCustomer) {
    return customers;
  }

  return [viewedCustomer, ...customers.filter(customer => customer.id !== viewedCustomerId)];
}

interface ILoadHouseAccountsProps {
  customerId: number;
  /** Display overlay-spinner while loading */
  globalLoader: boolean;
  /** Selected Account after loading HouseAccounts */
  selectedAccountIndex: number;
  token?: CancelToken;
}

export default function CustomerHouseAccounts() {
  const { t } = useTranslation();
  const history = useHistory();
  const windowSize = useWindowSize();
  const { StripeTerminalRefundSheet, processRefund } = useStripeTerminalPayment();

  const { state, updateState } = useCustomerContext();
  const dispatch = useAppDispatch();
  const { facilityStore, authStore } = useAppSelector(store => store);
  console.log("payment options", facilityStore.paymentOptions);

  const permissions = authStore.user?.permissions;

  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [refundModal, setRefundModal] = useState({
    isVisible: false,
    transactionId: null,
    paymentType: null,
    paymentIntentId: null,
    amount: null,
  });

  const [startingDate, setStartingDate] = useState(new Date());
  const [endingDate, setEndingDate] = useState(new Date());

  const [newAccountModalVisible, setNewAccountModalVisible] = useState(false);
  const [editAccountModalVisible, setEditAccountModalVisible] = useState(false);
  const [editAccountSheet, setEditAccountSheet] = useState<Partial<EditHouseAccountState>>({
    title: "",
    account_number: "",
    accounting_reference_id: null,
    limit: "",
    tournament_id: null,
    sendStatement: false,
    auto_pay_balance: false,
    auto_pay_anchor_date: null,
    default_payment_method_id: null,
  });
  const [printStatementModalVisible, setPrintStatementModalVisible] = useState(false);

  const [scheduledPaymentModalVisible, setScheduledPaymentModalVisible] = useState<boolean>(false);

  const [editScheduledPaymentModalVisible, setEditScheduledPaymentModalVisible] = useState<boolean>(false);

  const [cancelModal, setCancelModal] = useState({
    scheduledPaymentId: null,
    modalVisible: false,
  });

  const [editScheduledPayment, setEditScheduledPayment] = useState<any>(null);

  const [adjustAccountModalVisible, setAdjustAccountModalVisible] = useState<boolean>(false);
  const [manageCustomersDrawerOpen, setManageCustomersDrawerOpen] = useState(false);

  const [selectedTableTab, setSelectedTableTab] = useState<number>(0);

  const [selectedParentId, setSelectedParentId] = useState<number>(0);

  const [minimums, setMinimums] = useState<TMinimumSpend[]>(null);

  const {
    state: manualProcessModal,
    closeModal: closeManualProcessModal,
    updateModal: updateManualProcessModal,
  } = useModal({ scheduled_payment_id: null });

  const {
    state: addMinimumModal,
    closeModal: closeAddMinimumModal,
    updateModal: updateAddMinimumModal,
  } = useModal({ selected_minimum_id: null });

  const {
    state: removeMinimumModal,
    closeModal: closeRemoveMinimumModal,
    updateModal: updateRemoveMinimumModal,
  } = useModal();

  const {
    state: emailStatementModal,
    closeModal: closeEmailStatementModal,
    updateModal: updateEmailStatementModal,
  } = useModal<{ email: string; statementId: number }>({ email: "", statementId: null });

  const {
    state: editTransactionModal,
    closeModal: closeEditTransactionModal,
    updateModal: updateEditTransactionModal,
  } = useModal({ transaction: null, transactionDate: new Date(), transactionTime: "00:00" });

  const tableTabs = [
    {
      id: "statements",
      content: t("Statements"),
    },
    {
      id: "transactions",
      content: t("Transactions"),
    },
    {
      id: "scheduledPayments",
      content: t("Schedule payments"),
    },
  ];

  // load all HouseAccounts associated with customer
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (state.houseAccounts === null) {
      void loadHouseAccounts({
        customerId: state.customer.id,
        selectedAccountIndex: 0,
        globalLoader: false,
        token: source.token,
      });
    }

    if (facilityStore.paymentOptions.length === 0) {
      void dispatch(loadPaymentOptions());
    }

    if (!minimums) {
      void loadMinimums();
    }

    return () => {
      source.cancel("CustomerHouseAccounts unmounted.");
    };
  }, [state.customer.id]);

  // Update statements & transactions as side-effect of changing selected HouseAccount
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (state.selectedHouseAccount) {
      setEditAccountSheet({
        ...editAccountSheet,
        title: state.selectedHouseAccount.title,
        account_number: state.selectedHouseAccount.account_number,
        accounting_reference_id: state.selectedHouseAccount.accounting_reference_id,
        limit: state.selectedHouseAccount.limit as string,
        tournament_id: state.selectedHouseAccount.tournament_id,
        sendStatement: state.selectedHouseAccount.send_statement,
        auto_pay_balance: state.selectedHouseAccount.auto_pay_balance,
        auto_pay_anchor_date: state.selectedHouseAccount.auto_pay_anchor_date,
        default_payment_method_id: state.selectedHouseAccount.default_payment_method_id,
      });

      void loadCustomerStatements(source.token);
      void loadAccountTransactions(source.token);
      void loadScheduledPayments(source.token);
    } else {
      updateState({
        accountStatements: [],
        accountTransactions: [],
        accountScheduledPayments: [],
      });
    }

    return () => {
      source.cancel("CustomerHouseAccounts unmounted.");
    };
  }, [state.selectedHouseAccount]);

  async function loadHouseAccounts(args: ILoadHouseAccountsProps) {
    const { customerId, token, selectedAccountIndex } = args;

    const accountRes = await GetCustomerHouseAccounts(
      { customer_id: customerId, closed: false },
      token ? false : true,
      token,
    );
    if (accountRes.status !== StatusCode.OK) {
      return;
    }

    updateState({
      houseAccounts: accountRes.data,
      selectedHouseAccount:
        accountRes.data.length > 0 ? accountRes.data[selectedAccountIndex] ?? accountRes.data[0] : [],
      paymentAmount:
        accountRes.data.length > 0
          ? (accountRes.data[selectedAccountIndex] ?? accountRes.data[0])?.balance?.toString() ?? "0"
          : "0",
    });
  }

  async function loadCustomerStatements(token?: CancelToken) {
    if (state.accountStatements !== null) {
      updateState({ accountStatements: null }); // disable inputs
    }

    const statementRes = await GetCustomerStatements(
      { account_id: state.selectedHouseAccount.id },
      token ? false : true,
      token,
    );
    if (token && token.reason) {
      return;
    }
    if (statementRes.status !== StatusCode.OK) {
      return;
    }

    updateState({ accountStatements: statementRes.data }); // enable inputs
  }

  async function loadAccountTransactions(token?: CancelToken) {
    if (state.accountTransactions !== null) {
      updateState({ accountTransactions: null });
    }
    const accountTransactionsRes = await GetAccountTransactions(
      { account_id: state.selectedHouseAccount?.id },
      token ? false : true,
      token,
    );

    if (accountTransactionsRes.status !== StatusCode.OK) {
      if (token.reason) {
        console.log(token.reason.message);
      } else {
        dispatch(showError(t("Error retrieving Account Transactions.")));
      }
      return;
    }

    const markRefunds = determineRefundedTransactions(accountTransactionsRes.data as HouseAccountTransactionType[]);

    const facilities = facilityStore.facilities;

    for (let i = 0; i < markRefunds.length; i++) {
      for (let k = 0; k < facilities.length; k++) {
        if (markRefunds[i].facility_id === facilities[k].id) {
          markRefunds[i].facility = facilities[k];
        }
      }
    }

    updateState({ accountTransactions: markRefunds });
  }

  async function loadScheduledPayments(cancelToken?: CancelToken) {
    if (state.accountScheduledPayments !== null) {
      updateState({ accountScheduledPayments: null });
    }

    const paymentRes = await GetAccountScheduledPayments(
      { account_id: state?.selectedHouseAccount?.id },
      cancelToken ? false : true,
      cancelToken,
    );

    if (cancelToken && cancelToken.reason) {
      return;
    }
    if (paymentRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting account scheduled payments")));
      return;
    }

    updateState({
      accountScheduledPayments: [...(paymentRes?.data ?? [])].sort(sortScheduledPaymentsByLatest),
    });
  }

  async function refreshAccountBalance() {
    const balanceRes = await PutAccountBalanceRefresh({ account_id: state.selectedHouseAccount?.id }, true);
    if (balanceRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating account balance")));
    }
    const updatedAccounts = state.houseAccounts;
    updatedAccounts.map(account => {
      if (account.id === state.selectedHouseAccount.id) {
        return { ...balanceRes.data } as HouseAccountType;
      } else {
        return account;
      }
    });

    dispatch(showSuccess(t("Account balance refreshed successfully")));
    updateState({ selectedHouseAccount: balanceRes.data, houseAccounts: updatedAccounts });

    void loadCustomerStatements();
    void loadAccountTransactions();
  }

  async function downloadAccountStatement(statement_id: number) {
    const downloadRes = await DownloadCustomerAccountStatements({ account_statement_id: statement_id }, true);
    if (downloadRes.status !== StatusCode.OK) {
      dispatch(showError(downloadRes.data.message as string));
      return;
    }

    window.open().document.write(downloadRes.data as string);
  }

  async function printAccountStatementRange(accountId: number) {
    const params = {
      account_id: accountId,
      start_date: startingDate,
      end_date: endingDate,
    };

    const downloadRes = await DownloadCustomerAccountStatements(params, true);
    if (downloadRes.status !== StatusCode.OK) {
      return;
    }
    window.open().document.write(downloadRes.data as string);
  }

  async function getAccountTransactionReceipt(accountId: number) {
    const receiptRes = await GetAccountTransactionReceipt({ account_transaction_id: accountId }, false);
    if (receiptRes.status !== StatusCode.OK) {
      dispatch(showError(receiptRes.data.message as string));
      return;
    }
    window.open().document.write(receiptRes.data as string);
  }

  async function createHouseAccount(houseAccount: HouseAccountType, limitValid: boolean) {
    if (houseAccount.title === "" || !limitValid) {
      return;
    }

    const postAccount: IPostAccount = {
      title: houseAccount.title,
      currency: facilityStore?.facility?.default_currency_code ?? "cad",
      global: houseAccount.global,
      account_number: houseAccount.account_number === "" ? undefined : houseAccount.account_number,
      limit: Number(houseAccount.limit),
      accounting_reference_id: houseAccount.accounting_reference_id === 0 ? null : houseAccount.accounting_reference_id,
      customer_id: state.customer.id,
      default_payment_method_id: houseAccount?.default_payment_method_id,
      send_statement: houseAccount.send_statement ?? false,
      auto_pay_balance: houseAccount?.auto_pay_balance ?? false,
      auto_pay_anchor_date: houseAccount?.auto_pay_balance ? houseAccount.auto_pay_anchor_date : 30,
    };

    const accountRes = await PostAccount(postAccount, true);
    if (accountRes.status !== StatusCode.OK) {
      return;
    }

    console.log("PostAccount res ", accountRes); //debug

    setNewAccountModalVisible(false);
    void loadHouseAccounts({
      customerId: state.customer.id,
      globalLoader: true,
      selectedAccountIndex: state.houseAccounts.length,
    });
  }

  async function updateHouseAccount(editState: EditHouseAccountState) {
    const params: IPutAccount = {
      id: state.selectedHouseAccount.id,
      title: editState.title,
      account_number: editState.account_number,
      limit: Number(editState.limit),
      accounting_reference_id: editState.accounting_reference_id === 0 ? null : editState.accounting_reference_id,
      tournament_id: editState.tournament_id,
      global: editState.global,
      send_statement: editState.sendStatement,
      default_payment_method_id: editState?.default_payment_method_id,
      auto_pay_balance: editState?.auto_pay_balance,
      auto_pay_anchor_date: editState?.auto_pay_anchor_date,
    };

    const accountRes = await PutAccount(params, true);

    if (accountRes.status !== StatusCode.OK) {
      return;
    }

    setEditAccountModalVisible(false);
    void loadHouseAccounts({
      customerId: state.customer.id,
      globalLoader: true,
      selectedAccountIndex: state.houseAccounts.findIndex(account => {
        return account.id === state.selectedHouseAccount.id;
      }),
    });
  }

  async function handleInteracRefund() {
    // Process refund through terminal (to refund the transaction on Stripe)
    const refundRes = await processRefund(Number(refundModal?.amount), String(refundModal.paymentIntentId));
    if (refundRes === "success") {
      // When refund through terminal succeeds, process refund on tee on
      const refundRes = await PostAccountTransactionRefund(
        {
          account_transaction_id: refundModal?.transactionId,
        },
        true,
      );

      if (refundRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error processing refund")));
        return;
      }

      dispatch(showSuccess(t("Transaction refunded successfully")));

      setRefundModal({ isVisible: false, transactionId: null, paymentType: null, paymentIntentId: null, amount: null });
      void refreshAccountBalance();
    }
  }

  async function handleAccountTransactionRefund(transactionId: number) {
    // Handle interac (debit card) payment refunds
    if (refundModal?.paymentType === "interac") {
      await handleInteracRefund();
      return;
    }

    const refundRes = await PostAccountTransactionRefund(
      {
        account_transaction_id: transactionId,
      },
      true,
    );

    if (refundRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error processing refund")));
      return;
    }

    dispatch(showSuccess(t("Transaction refunded successfully")));

    setRefundModal({ isVisible: false, transactionId: null, paymentType: null, paymentIntentId: null, amount: null });
    void refreshAccountBalance();
  }

  async function updateHouseAccountTransaction() {
    if (!editTransactionModal.transaction) {
      dispatch(showError("Transaction not selected"));
      return;
    }

    const dateTime =
      moment.utc(editTransactionModal.transactionDate).local().format("YYYY-MM-DD") +
      " " +
      editTransactionModal.transactionTime;

    const processed_at = moment(dateTime, "YYYY-MM-DD HH:mm:ss").utc();

    const editPayload: { id: number; processed_at: string; payment_option_id?: number } = {
      id: editTransactionModal.transaction.id,
      processed_at: processed_at.format("YYYY-MM-DD HH:mm:ss"),
    };
    if (editTransactionModal.transaction.kind === "account_payment") {
      editPayload.payment_option_id = editTransactionModal.transaction.payment_option_id;
    }

    const editres = await PutAccountTransaction(editPayload, true);

    if (editres.status !== StatusCode.OK) {
      dispatch(showError(t("Error processing transaction")));
      return;
    }

    dispatch(showSuccess(t("Transaction updated successfully")));
    closeEditTransactionModal();
    void loadHouseAccounts({
      customerId: state.customer.id,
      globalLoader: true,
      selectedAccountIndex: state.houseAccounts.findIndex(account => {
        return account.id === state.selectedHouseAccount.id;
      }),
    });
  }

  async function handleCloseHouseAccount(accountId: number) {
    const res = await CloseHouseAccount({ account_id: accountId }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(`Error: ${res.data as string}`));
      return;
    }

    dispatch(showSuccess(t("Successfully closed account")));
    setCloseModal(false);

    void loadHouseAccounts({
      customerId: state.customer.id,
      globalLoader: true,
      selectedAccountIndex: state.houseAccounts.findIndex(account => {
        return account.id === state.selectedHouseAccount.id;
      }),
    });
  }

  function handleHouseAccountChange(accountId: number) {
    if (accountId !== null) {
      const account = state.houseAccounts.find(account => {
        return account.id === accountId;
      });

      updateState({
        selectedHouseAccount: account,
        paymentAmount: account?.balance.toString(),
      });
    }
  }

  function handleShowCancelScheduledPayment(id: number) {
    setCancelModal({ modalVisible: true, scheduledPaymentId: id });
  }

  async function cancelScheduledPayment() {
    const cancelRes = await CancelAccountScheduledPayment({ id: cancelModal.scheduledPaymentId }, true);

    if (cancelRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error cancelling payment")));
      return;
    }

    setCancelModal({ modalVisible: false, scheduledPaymentId: null });
    dispatch(showSuccess(t("Payment cancelled successfully")));
    void loadScheduledPayments();
  }

  const elementScrollRef = useCallback(
    element => {
      if (element !== null && selectedParentId) {
        if (element?.id === String(selectedParentId)) {
          element?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    },
    [selectedParentId],
  );

  function getStartOfPeriod(minimum: TMinimumSpend) {
    switch (minimum?.structure) {
      case "monthly":
        return moment().startOf("month");
      case "yearly":
        return moment().startOf("year");
      case "quarterly":
        return moment().startOf("quarter");
    }
  }

  function getEndOfPeriod(minimum: TMinimumSpend) {
    switch (minimum?.structure) {
      case "monthly":
        return moment().endOf("month");
      case "yearly":
        return moment().endOf("year");
      case "quarterly":
        return moment().endOf("quarter");
    }
  }

  function daysUntilEndOfPeriod(minimum: TMinimumSpend): number {
    switch (minimum?.structure) {
      case "monthly":
        return moment().endOf("month")?.diff(moment(), "days");
      case "yearly":
        return moment().endOf("year")?.diff(moment(), "days");
      case "quarterly":
        return moment().endOf("quarter")?.diff(moment(), "days");
    }
  }

  function minimumStateDisplay(minimum: TMinimumSpend) {
    const spendPercent = minimum.pivot?.balance / minimum?.amount;
    const dayPercent =
      1 -
      Number(daysUntilEndOfPeriod(minimum)) / Number(getEndOfPeriod(minimum)?.diff(getStartOfPeriod(minimum), "days"));

    if (spendPercent >= 1) {
      // good state
      return "complete";
    } else if (spendPercent < dayPercent && Number(daysUntilEndOfPeriod(minimum)) < 5) {
      // warning state
      return "warning";
    } else {
      // on track state
      return "on_track";
    }
  }

  async function loadMinimums() {
    const res = await GetMinimumSpending(null, true);

    if (res.status !== StatusCode.OK) {
      return;
    }

    setMinimums(res.data as TMinimumSpend[]);
  }

  async function addMinimum() {
    const res = await AddMinimumToAccount(
      { account_id: state.selectedHouseAccount.id, minimum_id: addMinimumModal.selected_minimum_id },
      true,
    );

    if (res.status !== StatusCode.OK) {
      return;
    }

    void loadHouseAccounts({
      customerId: state.customer.id,
      globalLoader: true,
      selectedAccountIndex: state.houseAccounts.findIndex(account => {
        return account.id === state.selectedHouseAccount.id;
      }),
    });

    closeAddMinimumModal();
  }

  async function removeMinimum() {
    const res = await RemoveMinimumFromAccount(
      { account_id: state.selectedHouseAccount.id, minimum_id: state.selectedHouseAccount.minimum?.id },
      true,
    );

    if (res.status !== StatusCode.OK) {
      return;
    }

    void loadHouseAccounts({
      customerId: state.customer.id,
      globalLoader: true,
      selectedAccountIndex: state.houseAccounts.findIndex(account => {
        return account.id === state.selectedHouseAccount.id;
      }),
    });

    closeRemoveMinimumModal();
  }

  async function processManualScheduledPayment() {
    const register = JSON.parse(localStorage.getItem("register"));
    const registerId = register?.id;

    const res = await ProcessScheduledPaymentManual(
      { scheduled_payment_id: manualProcessModal.scheduled_payment_id, register_id: registerId },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError("Error occurred when processing scheduled payment"));
      closeManualProcessModal();
      return;
    }

    void loadScheduledPayments();
    void refreshAccountBalance();

    dispatch(showSuccess("Payment processed successfully"));
    closeManualProcessModal();
  }

  async function emailAccountStatement(statementId: number) {
    const res = await EmailCustomerAccountStatement(
      { id: statementId, account_id: state.selectedHouseAccount.id, email: emailStatementModal?.email ?? undefined },
      true,
    );
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : "Error emailing statement"));
      return;
    }

    dispatch(showSuccess(t("Statement email sent successfully")));
    void closeEmailStatementModal();
  }

  function handleTransactionDatePickerChange(selectedDate: Date) {
    updateEditTransactionModal({ transactionDate: selectedDate });
  }

  return (
    <div>
      {state.houseAccounts ? (
        <>
          <div className="balance-credit-container">
            <div className="house-accounts_left">
              <div className="house-accounts-overview-header">
                <h2 className="house-accounts-overview-title">{t("Account overview")}</h2>
                <div className="house-accounts-summary-actions">
                  {state.houseAccounts.length > 0 ? (
                    <>
                      <NavigationDropdownNew
                        rightAlign
                        type="secondary"
                        menuFitContent
                        label={state.selectedHouseAccount?.title || t("Select account")}
                        disabled={state.accountStatements === null || state.accountTransactions === null}
                        sections={[
                          state.houseAccounts.map(account => ({
                            label: account?.closed ? (
                              <span>
                                {account.title}
                                &nbsp;
                                <Badge type="gray">{t("Closed")}</Badge>
                              </span>
                            ) : (
                              account.title
                            ),
                            customIcon: (
                              <FontAwesomeIcon
                                icon={faCheck}
                                className="navigation-dropdown-new-content-section-option-icon"
                                style={{
                                  opacity: account.id === state.selectedHouseAccount?.id ? 1 : 0,
                                }}
                              />
                            ),
                            onClick: () => handleHouseAccountChange(account.id),
                          })),
                        ]}
                      />
                      <NavigationDropdownNew
                        rightAlign
                        showPlainTextLabel
                        hideChevron
                        label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                        disabled={!state.selectedHouseAccount?.id}
                        sections={[
                          [
                            {
                              label: t("New Account"),
                              icon: "plus",
                              onClick: () => setNewAccountModalVisible(true),
                            },
                          ],
                          [
                            {
                              label: t("Make Payment"),
                              icon: "dollar-sign",
                              onClick: () => setPaymentModalVisible(true),
                              disabled: !state.selectedHouseAccount?.id,
                            },
                            {
                              label: t("Adjustment"),
                              icon: "arrow-up-arrow-down",
                              disabled: !permissions.accounts_post_adjustment || !state.selectedHouseAccount?.id,
                              onClick: () => setAdjustAccountModalVisible(true),
                            },
                            {
                              label: t("Schedule Payment"),
                              icon: "calendar-days",
                              onClick: () => setScheduledPaymentModalVisible(true),
                              disabled: !state.selectedHouseAccount?.id,
                            },
                          ],
                          [
                            {
                              label: t("Add Minimum Spend"),
                              icon: "plus",
                              onClick: () => updateAddMinimumModal({ isOpen: true }),
                              hidden: state.selectedHouseAccount.minimum !== null,
                            },
                            {
                              label: t("Remove Minimum Spend"),
                              icon: "trash",
                              onClick: () => updateRemoveMinimumModal({ isOpen: true }),
                              hidden:
                                state.selectedHouseAccount.minimum === null ||
                                state.selectedHouseAccount.minimum === undefined,
                            },
                            {
                              label: t("Edit Account"),
                              icon: "pencil",
                              onClick: () => setEditAccountModalVisible(true),
                              disabled: !state.selectedHouseAccount?.id,
                            },
                            {
                              label:
                                getAccountCustomers(state.selectedHouseAccount).length > 1
                                  ? t("Manage customers")
                                  : t("Add customer"),
                              icon:
                                getAccountCustomers(state.selectedHouseAccount).length > 1 ? "user-pen" : "user-plus",
                              onClick: () => setManageCustomersDrawerOpen(true),
                              disabled: !state.selectedHouseAccount?.id,
                            },
                            {
                              label: t("Close Account"),
                              icon: "lock",
                              onClick: () => setCloseModal(true),
                              disabled: !state.selectedHouseAccount?.id,
                            },
                            {
                              label: t("Merge Account"),
                              icon: "merge",
                              disabled: true, // Temporary until merge account is implemented
                              onClick: () => {},
                            },
                            {
                              label: t("Print Statement"),
                              icon: "print",
                              onClick: () => setPrintStatementModalVisible(true),
                              disabled: !state.selectedHouseAccount?.id,
                            },
                          ],
                        ]}
                      />
                    </>
                  ) : (
                    <Button
                      type="secondary"
                      size="medium"
                      className="house-accounts-new-account-button"
                      onClick={() => setNewAccountModalVisible(true)}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      {t("New account")}
                    </Button>
                  )}
                </div>
              </div>

              <div className="house-accounts-overview-divider" aria-hidden />

              <div className="house-accounts-overview-metrics">
                <div className="house-accounts_balance">
                  <div className="house-accounts_balance-icon">
                    <FontAwesomeIcon size="lg" icon={faWallet} />
                  </div>
                  <div className="house-accounts_balance_container">
                    <div className="house-accounts_balance_container-balance">
                      <LocaleCurrency amount={state.selectedHouseAccount?.balance ?? 0} />
                    </div>
                    <div className="house-accounts_balance_container-meta">
                      <div className="house-accounts_balance_container-description">{t("Current balance")}</div>
                      {state.selectedHouseAccount?.id ? (
                        <Button type="text" size="small" onClick={refreshAccountBalance} className="pt-0 pb-0">
                          {t("Refresh")}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="balance-divider" aria-hidden />
                <div className="house-accounts_balance">
                  <div className="house-accounts_balance-icon">
                    <FontAwesomeIcon size="lg" icon={faCreditCard} />
                  </div>
                  <div className="house-accounts_balance_container">
                    <div className="house-accounts_balance_container-balance">
                      <LocaleCurrency amount={state.selectedHouseAccount?.limit ?? 0} />
                    </div>
                    <div className="house-accounts_balance_container-description">{t("Credit limit")}</div>
                  </div>
                </div>
                {state.selectedHouseAccount?.minimum ? (
                  <>
                    <div className="balance-divider" aria-hidden />
                    <div className="house-accounts_balance">
                      <div className="progress-circle">
                        <ProgressCircle
                          progress={
                            (state.selectedHouseAccount?.minimum.pivot?.balance /
                              Number(state.selectedHouseAccount?.minimum?.amount)) *
                            100
                          }
                          size={64}
                          strokeWidth={6}
                        />
                      </div>
                      <div className="minimum-container">
                        <div className="minimum-header">{state.selectedHouseAccount?.minimum?.title}</div>
                        <div className="minimum-description">
                          <LocaleCurrency amount={state.selectedHouseAccount?.minimum.pivot?.balance} />
                          {t(" spent of ")}
                          <LocaleCurrency amount={state.selectedHouseAccount?.minimum?.amount} />
                        </div>
                        <div
                          className={`minimum-days-remaining ${minimumStateDisplay(
                            state.selectedHouseAccount?.minimum,
                          )}`}
                        >
                          {daysUntilEndOfPeriod(state.selectedHouseAccount?.minimum)}
                          {t(" days remaining")}
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
                {state.selectedHouseAccount?.id ? (
                  <>
                    <div className="balance-divider" aria-hidden />
                    <div className="house-accounts_balance house-accounts-overview-customers">
                      <div className="house-accounts_balance-icon">
                        <FontAwesomeIcon size="lg" icon={faUser} />
                      </div>
                      <div className="house-accounts_balance_container">
                        <div className="house-accounts-overview-customers-title">{t("Customer(s)")}</div>
                        {getAccountCustomers(state.selectedHouseAccount).length > 0 ? (
                          <AccountCustomersCell
                            account={{
                              ...state.selectedHouseAccount,
                              customers: getOverviewAccountCustomers(state.selectedHouseAccount, state.customer?.id),
                            }}
                            visibleCount={OVERVIEW_VISIBLE_CUSTOMER_NAMES}
                          />
                        ) : (
                          <span className="account-customers-cell__names">—</span>
                        )}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>

          <Card>
            <Card.Section>
              <div className="house-accounts-segmented-tabs">
                {tableTabs.map((tab, index) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={classNames("house-accounts-segmented-tabs_tab", {
                      "house-accounts-segmented-tabs_tab--first": index === 0,
                      "house-accounts-segmented-tabs_tab--last": index === tableTabs.length - 1,
                      "house-accounts-segmented-tabs_tab--active": selectedTableTab === index,
                    })}
                    onClick={() => setSelectedTableTab(index)}
                  >
                    {tab.content}
                  </button>
                ))}
              </div>

              <div
                className="house-accounts-table-scroll"
                style={{ display: selectedTableTab === 0 ? "block" : "none" }}
              >
                <DataTable<HouseAccountStatementType>
                  columns={[
                    { label: t("Statement") },
                    { id: "start_date", label: t("Start Date") },
                    { id: "end_date", label: t("End Date") },
                    { label: t("Balance") },
                    { label: "" },
                    { label: "" },
                  ]}
                  sort={(key, direction) => {
                    if (state.accountStatements) {
                      updateState({ accountStatements: sortObj(state?.accountStatements, key, direction) });
                    }
                  }}
                  loading={!state.accountStatements}
                  overflow
                >
                  {state.accountStatements?.map(statement => {
                    return (
                      <tr key={statement.id}>
                        <td>{statement.id}</td>
                        <td>{moment(statement.start_date).utc().format("LL")}</td>
                        <td>{moment(statement.end_date).utc().format("LL")}</td>
                        <td>
                          <LocaleCurrency currency={"CAD"} amount={statement.current_balance} />
                        </td>
                        <td>
                          <p onClick={() => downloadAccountStatement(statement.id)}>{t("Download")}</p>
                        </td>
                        <td>
                          <p
                            onClick={() =>
                              updateEmailStatementModal({
                                isOpen: true,
                                email: state?.selectedHouseAccount?.customer?.email,
                                statementId: statement?.id,
                              })
                            }
                          >
                            {t("Email")}
                          </p>
                        </td>
                      </tr>
                    );
                  })}
                </DataTable>
              </div>

              <div
                // className="house-accounts-table-scroll"
                style={{ display: selectedTableTab === 1 ? "block" : "none" }}
              >
                <DataTable<HouseAccountTransactionType>
                  className="overflow-visible"
                  columns={[
                    { label: t("Date") },
                    { label: t("Facility") },
                    { label: t("Amount") },

                    { label: t("Account") },
                    { label: t("Payment Method") },
                    { label: t("Last 4") },
                    { label: "", width: "5%" }, // 'Dropdown'
                  ]}
                  loading={!state.accountTransactions}
                  overflow
                >
                  {state.accountTransactions?.map(transaction => {
                    return (
                      <tr
                        key={transaction.id}
                        className={
                          transaction.id === selectedParentId ? "house-account-transaction-selected-parent" : null
                        }
                        id={String(transaction.id)}
                        ref={elementScrollRef}
                      >
                        <td>{moment(transaction?.processed_at).format("ll")}</td>

                        <td>{transaction?.facility?.long_name}</td>

                        <td>
                          <span
                            className={classNames({
                              "line-through": transaction?.voided_at !== null,
                            })}
                          >
                            <LocaleCurrency amount={transaction?.amount} currency={transaction?.currency} />{" "}
                            {transaction?.currency.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <p style={{ fontSize: "12px" }}>
                            {valueToString(transaction?.kind)} {transaction?.voided_at !== null && " - " + t("Voided")}
                          </p>
                          <p>{transactionBadge(transaction?.kind, t)}</p>
                        </td>
                        <td>
                          <span>{valueToString(transaction?.payment_type || "")}</span>
                        </td>
                        <td>{transaction.payment_last_4 ? transaction.payment_last_4 : ""}</td>
                        <td>
                          <NavigationDropdownNew
                            rightAlign
                            // position={index === 0 ? "bottom" : "top"}
                            showPlainTextLabel
                            label={<FontAwesomeIcon icon={["far", "chevron-down"]} />}
                            sections={[
                              [
                                {
                                  label: t("Print"),
                                  icon: "print",
                                  onClick: () => getAccountTransactionReceipt(transaction?.id),
                                  hidden: transaction?.kind !== "account_payment",
                                },
                                {
                                  label: t("Edit"),
                                  icon: "pen-to-square",
                                  onClick: () =>
                                    updateEditTransactionModal({
                                      isOpen: true,
                                      transaction: transaction,
                                      transactionDate: new Date(
                                        moment.utc(transaction?.processed_at).local().format("MMMM DD, YYYY"),
                                      ),
                                      transactionTime: moment.utc(transaction?.processed_at).local().format("HH:mm"),
                                    }),
                                  hidden: !["account_payment", "credit_adjustment", "debit_adjustment"].includes(
                                    transaction?.kind,
                                  ),
                                },
                                {
                                  label: transaction?.refunded ? "Refunded" : t("Refund"),
                                  icon: "turn-down-left",
                                  onClick: () =>
                                    setRefundModal({
                                      isVisible: true,
                                      transactionId: transaction.id,
                                      paymentType: transaction?.payment_type,
                                      paymentIntentId: transaction?.payment_intent_id,
                                      amount: transaction?.amount,
                                    }),
                                  disabled: transaction?.refunded || transaction?.kind !== "account_payment",
                                },
                                {
                                  label: t("View Order"),
                                  icon: "magnifying-glass",
                                  onClick: () =>
                                    history.push("/admin/order/" + String(transaction?.order_transaction?.order_id)),
                                  disabled: !transaction.order_transaction?.order_id,
                                },
                                {
                                  label: t("Highlight Original Transaction"),
                                  icon: "eye",
                                  onClick: () => setSelectedParentId(transaction.parent_id),
                                  hidden: !transaction.parent_id,
                                },
                              ],
                            ]}
                            informationSection={transaction?.note ? [transaction.note] : undefined}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </DataTable>
              </div>

              <div
                className="house-accounts-table-scroll"
                style={{ display: selectedTableTab === 2 ? "block" : "none" }}
              >
                <DataTable
                  columns={[
                    { id: "process_date", label: t("Process date") },
                    { label: t("Amount") },
                    { label: t("Status") },
                    { id: "processed_date", label: t("Processed On") },
                    { label: t("Note"), width: "40%" },
                    { label: "", width: "5%" },
                  ]}
                  sort={(key, direction) => {
                    if (state.accountScheduledPayments) {
                      updateState({
                        accountScheduledPayments: sortObj(state.accountScheduledPayments, key, direction),
                      });
                    }
                  }}
                  defaultSortDirection="desc"
                  loading={!state?.accountScheduledPayments}
                  overflow
                >
                  {state.accountScheduledPayments?.map((payment, index: number) => {
                    return (
                      <tr key={index}>
                        <td>{moment(payment?.process_date).format("ll")}</td>
                        <td>
                          <div className="house-accounts-scheduled-payment-amount">
                            <LocaleCurrency amount={payment?.amount} />
                            {payment?.charge_balance ? (
                              <p className="house-accounts-scheduled-payment-amount-secondary">
                                {t("Full statement balance")}
                              </p>
                            ) : null}
                          </div>
                        </td>
                        <td>{scheduledPaymentBadge(payment, t)}</td>
                        <td>{payment?.processed_date ? moment(payment.processed_date).format("ll") : ""}</td>
                        <td>{renderScheduledPaymentNote(payment, t)}</td>
                        <td className="house-accounts-scheduled-payment-actions">
                          {payment.status === "pending" ? (
                            <NavigationDropdownNew
                              rightAlign
                              position={index === 0 ? "bottom" : "top"}
                              hideChevron
                              type="secondary"
                              label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                              sections={[
                                [
                                  {
                                    label: t("Edit"),
                                    icon: "pen-to-square",
                                    onClick: () => {
                                      setEditScheduledPayment(payment);
                                      setEditScheduledPaymentModalVisible(true);
                                    },
                                  },
                                  {
                                    label: t("Process"),
                                    icon: "money-bill",
                                    onClick: () => {
                                      updateManualProcessModal({ isOpen: true, scheduled_payment_id: payment.id });
                                    },
                                  },
                                  {
                                    label: t("Cancel"),
                                    icon: "ban",
                                    onClick: () => handleShowCancelScheduledPayment(payment?.id),
                                  },
                                ],
                              ]}
                            />
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </DataTable>
              </div>
            </Card.Section>
          </Card>
        </>
      ) : (
        // Page Spinner
        <div style={{ marginTop: "4em", pointerEvents: "none", textAlign: "center" }}>
          <span style={{ display: "inline-flex", transform: "scale(2)" }}>
            <Spin />
          </span>
        </div>
      )}

      {/**
       *
       *  MODALS AND POPUPS
       *
       */}
      <NewCustomerHouseAccountModal
        isVisible={newAccountModalVisible}
        accountingReferenceTypes={state.accountingReferenceTypes}
        customerId={state.customer.id}
        onClose={() => setNewAccountModalVisible(false)}
        onOk={createHouseAccount}
      />

      <EditCustomerHouseAccountModal
        isVisible={editAccountModalVisible}
        accountingReferenceTypes={state.accountingReferenceTypes}
        editAccount={editAccountSheet}
        setEditAccount={setEditAccountSheet}
        onClose={() => {
          setEditAccountModalVisible(false);
          setEditAccountSheet({
            title: state.selectedHouseAccount?.title,
            account_number: state.selectedHouseAccount?.account_number,
            accounting_reference_id: state.selectedHouseAccount?.accounting_reference_id,
            limit: state.selectedHouseAccount?.limit as string,
            tournament_id: state.selectedHouseAccount?.tournament_id,
            auto_pay_anchor_date: state?.selectedHouseAccount?.auto_pay_anchor_date,
            auto_pay_balance: state?.selectedHouseAccount?.auto_pay_balance,
            sendStatement: state?.selectedHouseAccount?.send_statement,
          });
        }}
        onOk={updateHouseAccount}
      />

      {state.selectedHouseAccount && paymentModalVisible && (
        <HouseAccountPaymentModal
          paymentModalVisible={paymentModalVisible}
          houseAccount={state.selectedHouseAccount}
          paymentMethods={state.paymentMethods}
          onOk={() => void refreshAccountBalance()}
          closePaymentModal={() => setPaymentModalVisible(false)}
        />
      )}

      {state.selectedHouseAccount && closeModal && (
        <Popup
          open={true}
          type={"warning"}
          title={t("Close Account")}
          description={t("Are you sure you want to close this account?")}
          onCancel={() => setCloseModal(false)}
          onOk={() => handleCloseHouseAccount(state.selectedHouseAccount.id)} // TODO: Attach to an endpoint
        />
      )}

      <AccountScheduledPaymentModal
        open={scheduledPaymentModalVisible}
        onClose={() => setScheduledPaymentModalVisible(false)}
        onOk={loadScheduledPayments}
      />

      <EditAccountScheduledPaymentModal
        open={editScheduledPaymentModalVisible}
        onClose={() => setEditScheduledPaymentModalVisible(false)}
        onOk={loadScheduledPayments}
        scheduledPayment={editScheduledPayment}
      />

      <Popup
        open={cancelModal.modalVisible}
        type="warning"
        title={t("Cancel Payment?")}
        description={t("Are you sure want to cancel this scheduled payment? This can not be undone")}
        onOk={() => cancelScheduledPayment()}
        okText={t("Ok")}
        cancelText={t("Cancel")}
        onCancel={() => setCancelModal({ modalVisible: false, scheduledPaymentId: null })}
      />

      <Popup
        open={refundModal.isVisible}
        type="warning"
        title={t("Refund Transaction")}
        description={
          refundModal?.paymentType === "interac"
            ? t(
                "Are you sure you wish to refund this transaction? The customer will be prompted to use their debit card on the payment terminal to complete the refund.",
              )
            : t("Are you sure you wish to refund this transaction? This action cannot be undone.")
        }
        onOk={() => handleAccountTransactionRefund(refundModal.transactionId as number)}
        okText={t("Confirm")}
        cancelText={t("Cancel")}
        onCancel={() =>
          setRefundModal({
            isVisible: false,
            transactionId: null,
            paymentType: null,
            paymentIntentId: null,
            amount: null,
          })
        }
      />

      <HouseAccountAdjustModal
        open={adjustAccountModalVisible}
        onClose={() => setAdjustAccountModalVisible(false)}
        onOk={refreshAccountBalance}
      />

      <Sheet
        open={printStatementModalVisible}
        closable
        size="medium"
        title={t("Print Statement")}
        supportText={t("Choose a range to print the statement for.")}
        onOk={() => printAccountStatementRange(state.selectedHouseAccount.id)}
        okText={t("Print")}
        cancelText={t("Cancel")}
        onCancel={() => setPrintStatementModalVisible(false)}
        overflow
      >
        <DatePickerInput
          months={windowSize.width <= TABLET_WIDTH ? 1 : 2}
          position="center"
          showQuickOptions
          startingDate={startingDate}
          setStartingDate={setStartingDate}
          endingDate={endingDate}
          setEndingDate={setEndingDate}
        />
      </Sheet>

      <Portal isMounted={addMinimumModal.isOpen}>
        <Sheet
          open={addMinimumModal.isOpen}
          onOk={addMinimum}
          onCancel={closeAddMinimumModal}
          size="small"
          title={t("Add Minimum")}
          overflow
          okDisabled={!addMinimumModal.selected_minimum_id}
          okText={t("Add")}
        >
          <SelectNew
            label={t("Minimum")}
            id="new_minimum_select"
            options={minimums?.map(minimum => ({ label: minimum.title, id: minimum.id }))}
            onChange={value => updateAddMinimumModal({ selected_minimum_id: value })}
            value={addMinimumModal.selected_minimum_id}
          />
        </Sheet>
      </Portal>

      <Portal isMounted={removeMinimumModal.isOpen}>
        <Popup
          open={removeMinimumModal.isOpen}
          type="warning"
          title={t("Remove Minimum?")}
          description={t("Are you sure you want to remove this minimum?")}
          onOk={removeMinimum}
          onCancel={closeRemoveMinimumModal}
          okText={t("Remove")}
        />
      </Portal>

      <Portal isMounted={manualProcessModal.isOpen}>
        <Popup
          open={manualProcessModal.isOpen}
          type="warning"
          title={t("Process Payment")}
          description={t("Manually process this scheduled payment?")}
          onOk={processManualScheduledPayment}
          onCancel={closeManualProcessModal}
          okText={t("Continue")}
        />
      </Portal>

      <Portal isMounted={emailStatementModal.isOpen}>
        <Sheet
          title={t("Email Statement")}
          open={emailStatementModal?.isOpen}
          onCancel={closeEmailStatementModal}
          onOk={() => emailAccountStatement(emailStatementModal?.statementId)}
          okDisabled={!emailStatementModal?.email}
          okText={t("Send")}
          size="small"
        >
          <div className="p-1">
            <Input
              labelHidden
              onChange={e => updateEmailStatementModal({ email: e.target.value })}
              value={emailStatementModal?.email}
              placeholder={t("Email")}
            />
          </div>
        </Sheet>
      </Portal>

      <Portal>
        <Sheet
          title={t("Edit Transaction")}
          open={editTransactionModal?.isOpen}
          onCancel={closeEditTransactionModal}
          onOk={() => updateHouseAccountTransaction()}
          okDisabled={!editTransactionModal?.transaction}
          okText={t("Save")}
          size="small"
          overflow
        >
          <div className="p-1">
            {facilityStore.paymentOptions?.length > 0 &&
            facilityStore.paymentOptions?.find(
              paymentOption => paymentOption.id === editTransactionModal?.transaction?.payment_option_id,
            )?.type === "manual" ? (
              <div className="pb-2">
                <SelectNew
                  id="payment_method_select"
                  label={t("Payment Method")}
                  value={editTransactionModal?.transaction?.payment_option_id}
                  onChange={value =>
                    updateEditTransactionModal({
                      transaction: { ...editTransactionModal.transaction, payment_option_id: value },
                    })
                  }
                  options={facilityStore.paymentOptions
                    ?.filter(paymentOption => paymentOption.type === "manual")
                    ?.map(paymentOption => {
                      return {
                        label: paymentOption.title,
                        id: paymentOption.id,
                      };
                    })}
                />
              </div>
            ) : null}
            <DatePickerInput
              label={t("Date")}
              months={1}
              position="center"
              startingDate={editTransactionModal?.transactionDate}
              setStartingDate={handleTransactionDatePickerChange}
            />
          </div>
        </Sheet>
      </Portal>

      {/* Modal for refunding Interac transactions  */}
      <StripeTerminalRefundSheet />

      <ManageAccountCustomersDrawer
        open={manageCustomersDrawerOpen}
        account={state.selectedHouseAccount}
        pageCustomer={state.customer}
        onClose={() => setManageCustomersDrawerOpen(false)}
        onSaved={() => {
          const selectedIndex = state.houseAccounts.findIndex(account => account.id === state.selectedHouseAccount?.id);
          void loadHouseAccounts({
            customerId: state.customer.id,
            globalLoader: true,
            selectedAccountIndex: selectedIndex >= 0 ? selectedIndex : 0,
          });
        }}
      />
    </div>
  );
}

/** Refunded transaction based off parent_id of the transaction == the currentTransactionId */
function determineRefundedTransactions(transactions: HouseAccountTransactionType[]): HouseAccountTransactionType[] {
  const filteredTransactions = transactions.filter(
    (transaction: Record<string, any>) => transaction?.kind !== "authorization",
  );
  const refundedTransactions = transactions.filter(
    (transaction: Record<string, any>) => transaction?.parent_id && transaction?.kind === "account_refund",
  );

  const markRefunds = [...filteredTransactions]?.reduce(
    (markedRefunds, currentTransaction, index) => {
      const refunded = refundedTransactions?.some(
        (transaction: Record<string, any>) => transaction?.parent_id === currentTransaction?.id,
      );

      // Adding in 'refunded' param to HouseAccountTransactionType
      if (refunded) {
        markedRefunds[index] = { ...markedRefunds[index], refunded: true };
      } else {
        markedRefunds[index] = { ...markedRefunds[index], refunded: false };
      }
      return markedRefunds;
    },
    [...filteredTransactions],
  );

  return markRefunds;
}

// Account Payments and Refunds decrease balance on account
const transactionBadge = (type: TransactionKind, t: any) => {
  switch (type) {
    case "account_payment":
    case "refund":
    case "credit_adjustment":
      return (
        <Badge type="success" iconLeft={<FontAwesomeIcon icon={faArrowUpLeft} />}>
          {t("Credit")}
        </Badge>
      );
    case "debit_adjustment":
    case "sale":
      return (
        <Badge type="error" iconLeft={<FontAwesomeIcon icon={faArrowDownLeft} />}>
          {t("Debit")}
        </Badge>
      );
    default:
      return <></>;
  }
};

const sortScheduledPaymentsByLatest = (a: HouseAccountScheduledPayment, b: HouseAccountScheduledPayment): number => {
  const dateDiff = moment(b.process_date).valueOf() - moment(a.process_date).valueOf();
  if (dateDiff !== 0) {
    return dateDiff;
  }

  return (b.id ?? 0) - (a.id ?? 0);
};

const formatScheduledPaymentErrorMessage = (message?: string | null, errorCode?: string | null): string | null => {
  if (errorCode === "insufficient_funds" || /insufficient\s*funds/i.test(message ?? "")) {
    return "Insufficient funds";
  }

  return message || null;
};

const formatDeclinedScheduledPaymentNote = (
  payment: HouseAccountScheduledPayment,
  t: (key: string, options?: Record<string, string>) => string,
): string => {
  const reason = formatScheduledPaymentErrorMessage(payment.error_message, payment.error_code);
  const amount = LocaleCurrencyString({ amount: payment.amount });
  const date = moment(payment.process_date).format("ll");
  const nextAttemptDate = payment.next_attempt_date ? moment(payment.next_attempt_date).format("ll") : null;

  let content = reason
    ? t("The scheduled payment of {{amount}} on {{date}} has been declined due to {{reason}}", {
        amount,
        date,
        reason,
      })
    : t("The scheduled payment of {{amount}} on {{date}} has been declined.", {
        amount,
        date,
      });

  if (nextAttemptDate) {
    content = `${content} ${t("It will be retried on {{nextAttemptDate}}.", {
      nextAttemptDate,
    })}`;
  }

  return content;
};

const renderScheduledPaymentNote = (
  payment: HouseAccountScheduledPayment,
  t: (key: string, options?: Record<string, string>) => string,
) => {
  const existingNote = payment.note?.trim() ? payment.note : null;
  const declinedNote = payment.status === "failed" ? formatDeclinedScheduledPaymentNote(payment, t) : null;

  if (!existingNote && !declinedNote) {
    return null;
  }

  return (
    <div className="house-accounts-scheduled-payment-note">
      {existingNote ? <p className="house-accounts-scheduled-payment-note-text">{existingNote}</p> : null}
      {existingNote && declinedNote ? <hr className="house-accounts-scheduled-payment-note-divider" /> : null}
      {declinedNote ? <p className="house-accounts-scheduled-payment-note-text">{declinedNote}</p> : null}
    </div>
  );
};

const scheduledPaymentBadge = (payment: HouseAccountScheduledPayment, t: (key: string) => string) => {
  switch (payment.status) {
    case "complete":
      return <Badge type="success">{capitalize(payment.status)}</Badge>;
    case "canceled":
      return <Badge type="error">{capitalize(payment.status)}</Badge>;
    case "failed": {
      const declineReason = formatScheduledPaymentErrorMessage(payment.error_message, payment.error_code);
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <Badge type="error">{t("Declined")}</Badge>
          {declineReason ? (
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id={`scheduled-payment-declined-tooltip-${payment.id}`}>{declineReason}</Tooltip>}
            >
              <span style={{ display: "inline-flex", cursor: "pointer", color: "#98A2B3" }}>
                <FontAwesomeIcon icon={faQuestionCircle} />
              </span>
            </OverlayTrigger>
          ) : null}
        </span>
      );
    }
    default:
      return <Badge type="gray">{capitalize(payment.status)}</Badge>;
  }
};
